import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { PageHero } from "@/components/Shared";
import { IMAGES } from "@/lib/images";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const TABS = [
  { key: "all", label: "Tutte" },
  { key: "club", label: "Club Privé" },
  { key: "spa", label: "Spa Naturista" },
];

export default function Gallery() {
  const { gallery } = useSite();
  const [tab, setTab] = useState("all");
  const items = tab === "all" ? gallery : gallery.filter((g) => g.category === tab);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowRight") setOpenIndex((p) => (p + 1) % items.length);
      if (e.key === "ArrowLeft") setOpenIndex((p) => (p - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, items.length]);

  const next = (e) => { e?.stopPropagation(); setOpenIndex((p) => (p + 1) % items.length); };
  const prev = (e) => { e?.stopPropagation(); setOpenIndex((p) => (p - 1 + items.length) % items.length); };

  return (
    <div>
      <PageHero
        overline="Atmosfere"
        title="Gallery"
        subtitle="Un assaggio delle atmosfere che vivrai da Afrodite — eleganza, luci soffuse e discrezione."
        image={IMAGES.club_sil}
      />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex justify-center gap-3 mb-12 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`gallery-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-6 py-2.5 rounded-full text-sm tracking-wide border transition-all duration-300 ${
                tab === t.key
                  ? "bg-[#7A1426] border-[#D4AF37]/40 text-[#F3E5AB]"
                  : "border-[#D4AF37]/20 text-[#A39E9B] hover:text-[#FBF8F5]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-center text-[#6E6865] py-20">Nessuna immagine in questa sezione.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
            {items.map((it, i) => (
              <motion.div
                key={it.id}
                onClick={() => setOpenIndex(items.indexOf(it))}
                data-testid={`gallery-item-${it.id}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
                className="relative break-inside-avoid rounded-2xl overflow-hidden group border border-[#D4AF37]/10 cursor-pointer"
              >
                <img src={it.image_url} alt={it.caption || "Afrodite"}
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "linear-gradient(180deg, transparent, rgba(11,10,12,0.9))" }} />
                {it.caption && (
                  <div className="absolute bottom-0 inset-x-0 p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <span className="text-xs text-[#F3E5AB] tracking-wide uppercase">{it.caption}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {openIndex !== null && items[openIndex] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpenIndex(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/95"
            style={{ backdropFilter: "blur(20px)" }}
          >
            <button data-testid="lightbox-close" onClick={() => setOpenIndex(null)}
              className="absolute top-5 right-5 p-3 rounded-full text-[#FBF8F5] hover:text-[#D4AF37] transition-colors z-10"><X className="w-6 h-6" /></button>
            {items.length > 1 && (
              <>
                <button data-testid="lightbox-prev" onClick={prev}
                  className="absolute left-3 sm:left-8 p-3 rounded-full bg-black/40 text-[#FBF8F5] hover:text-[#D4AF37] border border-[#D4AF37]/20 transition-colors z-10"><ChevronLeft className="w-6 h-6" /></button>
                <button data-testid="lightbox-next" onClick={next}
                  className="absolute right-3 sm:right-8 p-3 rounded-full bg-black/40 text-[#FBF8F5] hover:text-[#D4AF37] border border-[#D4AF37]/20 transition-colors z-10"><ChevronRight className="w-6 h-6" /></button>
              </>
            )}
            <motion.figure
              key={items[openIndex].id}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full text-center"
            >
              <img src={items[openIndex].image_url} alt={items[openIndex].caption || "Afrodite"}
                className="max-h-[78vh] w-auto mx-auto rounded-xl object-contain shadow-2xl" />
              {items[openIndex].caption && (
                <figcaption className="mt-5 font-display text-lg sm:text-xl text-[#F3E5AB] tracking-wide">{items[openIndex].caption}</figcaption>
              )}
              <p className="mt-1 text-xs text-[#6E6865] tracking-widest">{openIndex + 1} / {items.length}</p>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
